import { reduxKey } from '@wix/da-react-context/pkg/publicSession/constants';
import { PublicSessionState, CoreLevel } from '@wix/da-react-context/pkg/publicSession';
import { ThemeOptions, ThemeSkin } from '@wix/da-react-context/pkg/ThemeContext';
import { PapiSession, PapiUser } from '@wix/da-papi-types';
import { getUserById } from '../redux/users/selectors';
import { HubChannel } from '../redux/hub/types';
import { createSelector } from 'reselect';
import { ThreadOrder } from '../CommentThread/types';
import get from 'lodash/get';

// TODO we should have a global typing for AppState that includes @@publicSession
// then this can be typed properly and usages can be updated to use the global AppState
export const getPublicSession = createSelector<
  any,
  any,
  Omit<PublicSessionState, 'user'> & { user: PapiUser }
>(
  state => state,
  state => {
    const publicSession: PublicSessionState = state[reduxKey] ?? undefined;
    let user;
    if (publicSession?.user) {
      if (typeof publicSession.user === 'object') {
        user = publicSession.user;
      } else {
        user = getUserById(state, publicSession.user);
      }
    }
    return {
      ...publicSession,
      user,
    };
  }
);

export const getCsrfToken = (state): string | undefined => getPublicSession(state).csrfToken;

export const getIsMobile = (state): boolean => getPublicSession(state).isMobile;

export const getIsIOS = (state): boolean => !!getPublicSession(state).device?.isIos;

/**
 * Made the assumption that mobile will never have paged mode, if this changes
 * in one instance I suggest making a getRawPagingMode type selector that would
 * get the user setting and not this semantic version
 */
export const getPagingMode = (state): 'scroll' | 'page' =>
  getPublicSession(state).pagingMode === 'page' && !getPublicSession(state).isMobile
    ? 'page'
    : 'scroll';

export const getPageSize = state => get(getPublicSession(state), 'pageSize', 24);

export const getIsLoggedIn = (state): boolean => getPublicSession(state).isLoggedIn;

export const getIsVerificationNeeded = state =>
  getIsLoggedIn(state) && !getPublicSession(state).isVerified;

export const getIsVerified = state => getIsLoggedIn(state) && getPublicSession(state).isVerified;

export const getIsUnverified = state =>
  getIsLoggedIn(state) && getPublicSession(state).isUnverified;

export const getVerificationEmailAddress = state => getPublicSession(state).verificationEmail;

export const getCurrentUser = (state): PapiUser => {
  const publicSession = getPublicSession(state);
  return publicSession.user;
};

export const getCurrentUserId = (state): number | undefined => {
  const currentUser = getCurrentUser(state);
  return currentUser && currentUser.userId;
};

export const getCurrentSessionId = (state): string | undefined => {
  const publicSession = getPublicSession(state);
  return publicSession.sessionId;
};
export const getSeo = state => getPublicSession(state).seo;

export const getSeoCanonical = state => {
  const seo = getSeo(state);

  return seo && seo.canonical;
};

export const getSeoTitle = state => {
  const seo = getSeo(state);
  return seo && seo.title
    ? seo.title
    : 'DeviantArt - Discover The Largest Online Art Gallery and Community';
};

export const getSeoDescription = state => {
  const seo = getSeo(state);
  return seo && seo.description;
};

export const getSeoPrevUrl = state => getSeo(state)?.prev;
export const getSeoNextUrl = state => getSeo(state)?.next;
export const getSeoRobots = state => getSeo(state)?.robots;
export const getSeoRating = state => getSeo(state)?.rating;

export const getCounts = (state): PapiSession['counts'] | undefined =>
  getPublicSession(state).counts;

export const getWatchCount = (state): number => (getCounts(state) && getCounts(state)!.watch) || 0;

export const getNotesCount = (state): number => (getCounts(state) && getCounts(state)!.notes) || 0;

export const getFeedbackCount = (state): number =>
  (getCounts(state) && getCounts(state)!.feedback) || 0;

export const getFeedbackUrl = (state): string => getPublicSession(state).feedbackUrl || '';

export const getCartCount = (state): number => (getCounts(state) && getCounts(state)!.cart) || 0;

export const getTierCount = (state): number => (getCounts(state) && getCounts(state)!.tiers) || 0;

export const getPointsCount = (state): number =>
  (getCounts(state) && getCounts(state)!.points) || 0;

export const getDywHasNew = (state): boolean =>
  (getCounts(state) && getCounts(state)!.dywHasNew) || false;

export const getCurrentUserHasCore = (state): boolean => getPublicSession(state).hasCoreMembership;

export const getCoreLevel = (state): CoreLevel => getPublicSession(state).coreLevel;

export const getCoreExpires = (state): string | undefined => getPublicSession(state).coreExpires;

export const getHasJoinedWithEclipse = (state): boolean =>
  getPublicSession(state).hasJoinedWithEclipse;

export function getViewMode(state) {
  return getPublicSession(state).deviationViewMode;
}

export const getHubState = (state: PublicSessionState) => getPublicSession(state).hub;

export const getLiveNotificationsEnabled = (state): boolean => {
  const hubState = getHubState(state);
  const subs = hubState && hubState.subs;
  return subs?.some(sub => sub.startsWith(`${HubChannel.NOTIFICATIONS}#`)) ?? false;
};

export const getDRE = (state): PapiSession['dre'] => getPublicSession(state).dre;

export const getShowMatureContent = createSelector(
  [getPublicSession],
  (session: PublicSessionState): boolean => session.showMatureContent
);
export const getContentFilter = createSelector(
  [getPublicSession],
  (session: PublicSessionState): PapiSession['user']['contentFilter'] => session.contentFilter
);

export const isUserOlderThan18 = createSelector(
  [getCurrentUser],
  (user: PapiUser): boolean => get(user, 'details.age', 0) >= 18
);

export const getCurrentUserCommentsOrder = createSelector(
  [getPublicSession],
  (session: PublicSessionState): ThreadOrder => {
    if (!session || !session.commentsOrder) {
      return 'newest';
    }
    return session.commentsOrder;
  }
);

export const getTheme = createSelector(
  [getPublicSession],
  (session: PublicSessionState): ThemeOptions => (session && session.theme) || ThemeOptions.DARK
);

export const getThemeSkin = createSelector(
  [getPublicSession],
  (session: PublicSessionState): ThemeSkin | undefined => session && session.themeSkin
);

export const getPageThumb = createSelector(
  [getPublicSession],
  (session: PublicSessionState): string | undefined => get(session, 'seo.socialThumbSrc')
);

export const getVerificationLoadingState = createSelector(
  [getPublicSession],
  ({ updateProgress = {} }: PublicSessionState) => ({
    isResendInProgress: !!updateProgress.isResendInProgress,
    isEmailChangeInProgress: !!updateProgress.isEmailChangeInProgress,
    isSendingDone: !!updateProgress.isSendingDone,
    updateFailed: !!updateProgress.updateFailed,
  })
);

export const getIsEclipseSwitcherEnabled = createSelector(
  [getPublicSession],
  (session: PublicSessionState) => {
    if (!session || !session.eclipseSwitcher) {
      return false;
    }
    return session.eclipseSwitcher;
  }
);

export const getDeviceSupportsWebp = (state): boolean => {
  const publicSession = getPublicSession(state);
  return get(publicSession, 'device.supportsWebp') ?? false;
};

export const getDeviceSupportsLazy = (state): boolean => {
  const publicSession = getPublicSession(state);
  return get(publicSession, 'device.supportsLazy') ?? false;
};

// Chat
export const getChatIsMuted = (state): boolean => {
  const publicSession = getPublicSession(state);
  return get(publicSession, 'chat.muted', false) === true;
};

export const getChatOnlineState = (state): boolean => {
  const publicSession = getPublicSession(state);
  return get(publicSession, 'chat.online', true);
};

export const getChatSoundsMuted = (state): boolean => {
  const publicSession = getPublicSession(state);
  return get(publicSession, 'chat.muteSound', false);
};

export const getCanUserSeeWatch = state => {
  // T58152: Enable old watch for all users
  return getPublicSession(state).isLoggedIn;
};

export const getPromos = createSelector(
  [getPublicSession],
  (session: PublicSessionState): PublicSessionState['promos'] => session?.promos
);

export const getLimitedBadgesPromo = createSelector(
  [getPublicSession],
  (session: PublicSessionState): PublicSessionState['limitedBadgesPromo'] =>
    session?.limitedBadgesPromo
);
